import { useCallback, useEffect, useRef, useState } from "react";
import { Studio } from "./Studio";
import { ToolPanel } from "./ToolPanel";
import { WheelPanel } from "./WheelPanel";
import { LoadScreen } from "./LoadScreen";
import { BottomHint } from "./BottomHint";
import { Chrome } from "./Chrome";
import {
  BRUSH_DEFAULT,
  BRUSH_MAX,
  BRUSH_MIN,
  CAMERA_Z_MAX,
  CAMERA_Z_MIN,
  CLAY_COLORS,
  type ClayColorId,
  type ToolId,
} from "./constants";
import { useIsMobile } from "./useIsMobile";

export default function Home() {
  const [loaded, setLoaded] = useState(false);
  const [interacted, setInteracted] = useState(false);
  const [tool, setTool] = useState<ToolId>("shape");
  const [colorId, setColorId] = useState<ClayColorId>("terracotta");
  const [speedPct, setSpeedPct] = useState(15);
  const [brushSize, setBrushSize] = useState(BRUSH_DEFAULT);
  const [liveMsg, setLiveMsg] = useState("");
  const isMobile = useIsMobile();

  // Refs bridged into Studio for hot-path state
  const speedRef = useRef(15 / 100);
  const cameraZoomTargetRef = useRef((CAMERA_Z_MIN + CAMERA_Z_MAX) * 0.55);
  const flashRef = useRef<() => void>(() => {});
  const resetRef = useRef<() => void>(() => {});
  const undoRef = useRef<() => void>(() => {});

  useEffect(() => {
    speedRef.current = speedPct / 100;
  }, [speedPct]);

  useEffect(() => {
    if (isMobile) {
      cameraZoomTargetRef.current = CAMERA_Z_MAX * 0.75;
    }
  }, [isMobile]);

  const clayColorHex = CLAY_COLORS.find((c) => c.id === colorId)?.hex ?? "#C67C5C";

  const markInteracted = useCallback(() => setInteracted(true), []);

  // Tool click handler — special case reset
  const handleTool = useCallback(
    (t: ToolId) => {
      markInteracted();
      if (t === "reset") {
        resetRef.current();
        // keep the previously active sculpt tool
        return;
      }
      setTool(t);
    },
    [markInteracted],
  );

  const handleColor = useCallback(
    (id: ClayColorId) => {
      markInteracted();
      setColorId(id);
      // Smooth color transition is handled inside the Clay material by lerping — trigger a flash pulse.
      flashRef.current();
      const c = CLAY_COLORS.find((x) => x.id === id);
      if (c) setLiveMsg(`Clay color set to ${c.label}.`);
    },
    [markInteracted],
  );

  const handleSpeed = useCallback(
    (v: number) => {
      markInteracted();
      setSpeedPct(v);
      setLiveMsg(`Wheel speed ${Math.round(v)} percent.`);
    },
    [markInteracted],
  );

  const handleBrush = useCallback(
    (v: number) => {
      markInteracted();
      setBrushSize(v);
    },
    [markInteracted],
  );

  const handleUndo = useCallback(() => {
    markInteracted();
    undoRef.current();
  }, [markInteracted]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.ctrlKey || e.metaKey;
      if (meta && e.key.toLowerCase() === "z") {
        e.preventDefault();
        handleUndo();
        return;
      }
      if (e.key === "1") { handleTool("shape"); return; }
      if (e.key === "2") { handleTool("pull"); return; }
      if (e.key === "3") { handleTool("smooth"); return; }
      if (e.key === "4") { handleTool("reset"); return; }
      if (e.key === "+" || e.key === "=") {
        handleBrush(Math.min(BRUSH_MAX, brushSize + 0.5));
        return;
      }
      if (e.key === "-" || e.key === "_") {
        handleBrush(Math.max(BRUSH_MIN, brushSize - 0.5));
        return;
      }
      if (e.key === "[") {
        handleSpeed(Math.max(0, speedPct - 5));
        return;
      }
      if (e.key === "]") {
        handleSpeed(Math.min(100, speedPct + 5));
        return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [brushSize, speedPct, handleTool, handleBrush, handleSpeed, handleUndo]);

  return (
    <main
      className="fixed inset-0 overflow-hidden select-none"
      style={{ background: "#1A1816" }}
    >
      {/* Screen reader live region */}
      <div className="sr-only" aria-live="polite" role="status">
        {liveMsg}
      </div>

      {/* 3D layer */}
      <div className="absolute inset-0 z-0">
        <Studio
          clayColor={clayColorHex}
          onColorFlashRequest={(fn) => {
            flashRef.current = fn;
          }}
          speedRef={speedRef}
          tool={tool}
          brushSize={brushSize}
          onReadyForReset={(fn) => {
            resetRef.current = fn;
          }}
          onReadyForUndo={(fn) => {
            undoRef.current = fn;
          }}
          cameraZoomTargetRef={cameraZoomTargetRef}
          announce={(m) => setLiveMsg(m)}
        />
      </div>

      {/* UI layer */}
      <Chrome />
      <ToolPanel
        activeTool={tool}
        onTool={handleTool}
        brushSize={brushSize}
        onBrushSize={handleBrush}
        onUndo={handleUndo}
      />
      <WheelPanel
        speedPct={speedPct}
        onSpeedPct={handleSpeed}
        color={colorId}
        onColor={handleColor}
      />
      <BottomHint interacted={interacted} />

      {/* Load screen last so it overlays everything until done */}
      {!loaded && <LoadScreen onDone={() => setLoaded(true)} />}
    </main>
  );
}